import {FC} from 'react'
import {Bug, AlertCircle, CalendarCheck, FileText, ClipboardList, Award} from 'lucide-react'
import type {QhseDerivedKpis, QhseDashboardData} from './types'
import {kpiRecords, monthlyCounts, certificateExpiryBuckets} from './dashboardData'
import {KpiCardSkeleton} from '../../../../shared/components/LoadingSkeletons'

const cards = [
  {key: 'openDefects', label: 'Open Defects', icon: Bug, note: 'Requires action', color: '#ef4444', background: '#fff1f2'},
  {key: 'nearMissesYTD', label: 'Near Misses (YTD)', icon: AlertCircle, note: 'Year to date', color: '#f59e0b', background: '#fffbeb'},
  {key: 'openAudits', label: 'Open Audits', icon: CalendarCheck, note: 'Not finalized', color: '#3b82f6', background: '#eff6ff'},
  {key: 'openWorkPermits', label: 'Open Work Permits', icon: FileText, note: 'Active permits', color: '#6366f1', background: '#eef2ff'},
  {key: 'pendingInspections', label: 'Pending Inspections', icon: ClipboardList, note: 'Not finalized', color: '#f43f5e', background: '#fff1f2'},
  {key: 'expiringCerts', label: 'Expiring Certs', icon: Award, note: 'Pending renewal', color: '#14b8a6', background: '#f0fdfa'},
] as const

const KpiCards: FC<{loading: boolean; kpis: QhseDerivedKpis; data: QhseDashboardData; onKpiClick: (kpi: string) => void}> = ({loading, kpis, data, onKpiClick}) => {
  const records = kpiRecords(data)
  const series = {
    openDefects: monthlyCounts(records.openDefects.map(d => d.dateObserved || d.createdAt)),
    nearMissesYTD: monthlyCounts(records.nearMissesYTD.map(n => n.dateOfOccurrence || n.dateIssued || n.createdAt)),
    openAudits: monthlyCounts(records.openAudits.map(a => a.auditFromDate)),
    openWorkPermits: monthlyCounts(records.openWorkPermits.map(w => w.startDate || w.createdAt)),
    pendingInspections: monthlyCounts(records.pendingInspections.map(i => i.inspectionFromDate)),
    expiringCerts: certificateExpiryBuckets(records.expiringCerts),
  }
  return <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5 md:gap-6'>
    {cards.map(card => loading ? <KpiCardSkeleton key={card.key} /> : (
      <div key={card.key} className='flex flex-col bg-white rounded-2xl p-4 border border-slate-200 shadow-sm'>
        <span className='text-[10px] text-slate-500'>{card.label}</span>
        <button aria-label={`View ${card.label}`} disabled={kpis[card.key] == null} onClick={() => onKpiClick(card.key)} className='text-left mt-2 bg-transparent border-0 p-0'>
          <span className='block text-2xl font-bold text-slate-900'>{kpis[card.key] ?? 'Unavailable'}</span>
          <span className='text-[10px] flex items-center gap-1 mt-1' style={{color: card.color}}><card.icon className='w-3 h-3' />{card.note}</span>
          {kpis[card.key] != null && <Sparkline label={`${card.label}: ${card.key === 'expiringCerts' ? 'expiry schedule' : 'record dates over the last six months'}`}
            values={series[card.key]} color={card.color} background={card.background} />}
        </button>
      </div>
    ))}
  </div>
}

const Sparkline: FC<{label: string; values: {label: string; count: number}[]; color: string; background: string}> = ({label, values, color, background}) => {
  const max = Math.max(1, ...values.map(value => value.count))
  const points = values.map((value, index) => `${index / (values.length - 1) * 100},${27 - value.count / max * 23}`).join(' ')
  const description = `${label}. ${values.map(value => `${value.label}: ${value.count}`).join('; ')}`
  return <span className='block mt-2 h-10 rounded-lg overflow-hidden' style={{background}}>
    <svg role='img' aria-label={description} viewBox='0 0 100 32' preserveAspectRatio='none' className='w-full h-full'>
      <title>{description}</title>
      <polyline points={points} fill='none' stroke={color} strokeWidth='1.5' vectorEffect='non-scaling-stroke' />
    </svg>
  </span>
}
export default KpiCards
